import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBrain, FaRobot, FaServer, FaDatabase,
  FaArrowRight, FaCheckCircle, FaLock,
  FaNetworkWired, FaComments, FaSearch,
  FaTerminal, FaCogs, FaLightbulb, FaHandshake,
  FaClipboardCheck, FaShieldAlt, FaUserShield
} from 'react-icons/fa';
import './AIPage.css';

const AIPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    {
      icon: FaComments,
      title: 'AI Chat na mieru',
      tag: 'LLM',
      description: 'Lokálny chatbot trénovaný na vašich firemných dátach. Pozná vaše produkty, procesy a dokumentáciu — odpovedá okamžite a presne.',
      techStack: ['Llama', 'Mistral', 'Ollama'],
      features: ['Interný helpdesk a podpora', 'Znalostná báza firmy', 'Onboarding nových zamestnancov', 'Zákaznícky chatbot na web']
    },
    {
      icon: FaSearch,
      title: 'RAG – Retrieval Augmented Generation',
      tag: 'RAG',
      description: 'AI, ktorá hľadá odpovede priamo vo vašich dokumentoch, zmluvách, manuáloch a interných databázach — nie z internetu.',
      techStack: ['LangChain', 'ChromaDB', 'Embeddings'],
      features: ['Vyhľadávanie v dokumentoch', 'Analýza zmlúv a faktúr', 'Automatické sumarizácie', 'Citácie zdrojov v odpovediach']
    },
    {
      icon: FaDatabase,
      title: 'Text-to-SQL',
      tag: 'SQL',
      description: 'Pýtajte sa databázy ľudskou rečou. AI preloží vašu otázku do SQL dotazu a vráti výsledky — bez znalosti programovania.',
      techStack: ['SQL Server', 'MySQL', 'NL2SQL'],
      features: ['Otázky v slovenčine/češtine', 'Napojenie na existujúce DB', 'Automatické reporty', 'Bezpečné read-only prístupy']
    },
    {
      icon: FaRobot,
      title: 'Automatizácia procesov',
      tag: 'AUTO',
      description: 'AI spracuje emaily, faktúry, objednávky a ďalšie dokumenty automaticky — bez manuálneho zásahu.',
      techStack: ['Python', 'OCR', 'NLP'],
      features: ['Spracovanie faktúr a dokladov', 'Extrakcia dát z PDF/email', 'Triedenie a kategorizácia', 'Napojenie na ERP systémy']
    }
  ];

  const techStack = [
    { name: 'Llama 3, Mistral, GPT-OS', category: 'Model' }, // V jednom řádku pro lepší přehlednost gpt os a mistral jsou menší modely, které se často používají pro on-premise nasazení
    { name: 'Ollama', category: 'Runtime' },
    { name: 'Semantic Kernel', category: 'Framework' },
    { name: 'ChromaDB', category: 'Vector DB' },
    { name: 'C#', category: 'Backend' },
    { name: 'REST API', category: 'API' },
    { name: 'SQL Server', category: 'Database' },
    { name: 'Inver Server', category: 'Deploy' },
    { name: 'React', category: 'Frontend' },
  ];

  const workflow = [
    {
      step: 1,
      title: 'Analýza procesov',
      description: 'Zmapujeme vaše workflow a identifikujeme, kde AI ušetrí najviac času a peňazí.',
      icon: FaClipboardCheck
    },
    {
      step: 2,
      title: 'Návrh architektúry',
      description: 'Navrhneme konkrétne riešenie — výber modelu, infraštruktúry a integračných bodov.',
      icon: FaLightbulb
    },
    {
      step: 3,
      title: 'Implementácia on-premise',
      description: 'Nasadíme AI na vaše servery. Vaše dáta nikdy neopustia vašu sieť.',
      icon: FaCogs
    },
    {
      step: 4,
      title: 'Školenie a podpora',
      description: 'Naučíme váš tím pracovať s AI a poskytneme dlhodobú technickú podporu.',
      icon: FaHandshake
    }
  ];

  return (
    <div className="ai-page">
      {/* Hero Section */}
      <section className="ai-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">On-Premise AI</div>
              <h1>Lokálne AI riešenia</h1>
              <h2>Chat · RAG · Text-to-SQL</h2>
              <p>
                Staviame AI systémy, ktoré bežia u vás na serveroch — offline, bezpečne, 
                bez závislosti na cloude. Vaše firemné dáta nikdy neopustia vašu infraštruktúru.
              </p>
              <div className="hero-actions">
                <Link to="/kontakt" className="btn btn-primary">
                  <FaArrowRight />Konzultácia zadarmo
                </Link>
              </div>
            </div>
            <div className="hero-image">
              <div className="ai-icon-container">
                <FaBrain className="ai-hero-icon" />
                <div className="ai-orbit ai-orbit-1"><FaServer /></div>
                <div className="ai-orbit ai-orbit-2"><FaDatabase /></div>
                <div className="ai-orbit ai-orbit-3"><FaTerminal /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="ai-services-section">
        <div className="container">
          <h2>Čo konkrétne staviame</h2>
          <p className="section-subtitle">
            Reálne AI riešenia nasadené u našich klientov — nie marketingové sľuby
          </p>
          <div className="services-grid">
            {coreServices.map((service, index) => (
              <div key={index} className="ai-service-card">
                <div className="ai-service-header">
                  <div className="ai-service-icon">
                    <service.icon />
                  </div>
                  <span className="ai-service-tag">{service.tag}</span>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="ai-service-stack">
                  {service.techStack.map((tech, i) => (
                    <span key={i} className="stack-chip">{tech}</span>
                  ))}
                </div>
                <ul className="ai-service-features">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <FaCheckCircle />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why On-Premise Section */}
      <section className="ai-onpremise-section">
        <div className="container">
          <h2>Prečo lokálne AI?</h2>
          <p className="section-subtitle">
            Cloud AI posiela vaše dáta tretím stranám. My to robíme inak.
          </p>
          <div className="onpremise-grid">
            <div className="onpremise-card">
              <div className="onpremise-icon"><FaLock /></div>
              <h4>Dáta zostávajú u vás</h4>
              <p>Žiadne firemné dokumenty, zmluvy ani know-how neodchádzajú z vašej siete. Nulové riziko úniku.</p>
            </div>
            <div className="onpremise-card">
              <div className="onpremise-icon"><FaNetworkWired /></div>
              <h4>Funguje offline</h4>
              <p>AI beží na vašom serveri — nepotrebuje internet. Funguje aj pri výpadku konektivity.</p>
            </div>
            <div className="onpremise-card">
              <div className="onpremise-icon"><FaShieldAlt /></div>
              <h4>GDPR natívne</h4>
              <p>Osobné údaje neopúšťajú EÚ. Spĺňate regulácie automaticky bez extra opatrení.</p>
            </div>
            <div className="onpremise-card">
              <div className="onpremise-icon"><FaUserShield /></div>
              <h4>Plná kontrola</h4>
              <p>Vy vlastníte model, dáta aj infraštruktúru. Žiadne mesačné poplatky za API, žiadny vendor lock-in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="ai-stack-section">
        <div className="container">
          <h2>Technológie, s ktorými pracujeme</h2>
          <div className="stack-grid">
            {techStack.map((tech, index) => (
              <div key={index} className="stack-card">
                <span className="stack-category">{tech.category}</span>
                <span className="stack-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="ai-workflow-section">
        <div className="container">
          <h2>Ako to prebieha</h2>
          <div className="workflow-grid">
            {workflow.map((item, index) => (
              <div key={index} className="workflow-card">
                {/* <div className="workflow-step">{item.step}</div> */}
                <div className="workflow-icon">
                  <item.icon />
                </div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ai-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Chcete AI, ktorá beží u vás?</h2>
            <p>
              Dohodnite si bezplatnú konzultáciu. Zanalyzujeme vaše procesy 
              a navrhneme konkrétne riešenie — bez záväzkov.
            </p>
            <Link to="/kontakt" className="btn btn-primary btn-large">
              <FaArrowRight />Chcem konzultáciu
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIPage;
